import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useNavigation } from 'react-router-dom';
import { myFirebaseAuth } from './firebase/config';
import { getUserDetails, logoutUser } from './redux/services/authService';
import { useAppDispatch, useAppSelector } from './redux/store';
import LogRocket from 'logrocket';
import {
  buildSupplierPath,
  getLoginSupplierId,
  getNavSegmentSection,
  handleSavePastPath,
  hasCreditAppRole,
  isCreditAppRestricted,
  isNavSegmentDisabled,
  sortMainBuyersByLoginSupplier,
  stripSupplierPrefix,
} from './utils/helpers';
import { useParamMainBuyerId } from './utils/hooks/useParamMainBuyerId';
import { getReleaseBySupplier } from './redux/services/liensService';

const LEGACY_BASE_PATHS = ['/buyerLienReleases', '/companies'];

const LEGACY_SUB_PATHS = [
  'overview',
  'lienReleasesSupplier',
  'lienReleasesJobs',
  'templates',
  'templateFields',
  'invoices',
  'customers',
  'payments',
  'invoices',
];

// Supplier sections used to be top-level routes (e.g. `/preliminaryNotices/overview`,
// `/supplierJobs/:id`, `/payments`). They now live under `/supplier/:supplierId/...`, so any
// previously-sent email deep-link that still uses the bare path no longer matches a route.
// We detect those bare paths and re-navigate with the `/supplier/:supplierId` prefix.
// `profile` is intentionally excluded because `/profile` is still a valid top-level route.
const SUPPLIER_SCOPED_SEGMENTS = [
  'supplierJobs',
  'invoices',
  'salesReceipts',
  'creditApplications',
  'tradeReferencesPool',
  'dashboardCreditApplications',
  'dashboardPayments',
  'approvalWorkflows',
  'creditMonitoring',
  'customers',
  'reports',
  'settings',
  'payments',
  'cashApplication',
  'virtualPOS',
  'preliminaryNotices',
  'liens',
  'noticeTemplates',
  'noticeTemplateFields',
  'lienTemplates',
  'lienTemplateFields',
  'paymentRequests',
  'disputes',
  'collectionTasks',
  'promiseToPays',
  'invoiceDelivery',
  'creditApplicationRequestTemplates',
];

export const ProtectedRoute = ({ children }: { children: any }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, firebaseUserId, isPasswordSet } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const basicSupplier = useAppSelector((state) => state.buyers.basicSupplier);
  const location = useLocation();
  const navigation = useNavigation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const { isPreviewMode, previewMainBuyer } = useAppSelector((state) => state.preview);
  const supplierId = currentSupplier?.id ?? user?.linkedSupplierIds?.[0];
  const supplierLandingPath = (subPath: string) =>
    supplierId
      ? buildSupplierPath(supplierId, subPath)
      : `/supplier?redirectTo=${encodeURIComponent(subPath)}`;

  // Auto-adjust legacy supplier deep-links from already-sent emails. If the path is a bare
  // supplier-scoped section (no `/supplier/:supplierId` prefix) we re-navigate with the prefix
  // so the route resolves. Runs before the role-based redirects below.
  useEffect(() => {
    if (!supplierId) {
      return;
    }
    const mainPath = location.pathname;
    if (mainPath.startsWith('/supplier/')) {
      return;
    }
    const firstSegment = mainPath.split('/').filter(Boolean)[0];
    if (firstSegment && SUPPLIER_SCOPED_SEGMENTS.includes(firstSegment)) {
      navigate(buildSupplierPath(supplierId, mainPath) + location.search, { replace: true });
    }
  }, [location.pathname, location.search, supplierId]);

  // A tab the sidebar locks is a route the user can't open by typing it either. Credit app users
  // (created with SUPPLIER + SUB_SUPPLIER alongside their credit app role, and no AR role) only
  // have the credit apps section, so they go back there. Users with no credit app role at all are
  // kept out of the credit section and sent to whichever landing `/supplier` resolves for them.
  useEffect(() => {
    if (!user || !isLoggedIn) {
      return;
    }
    const segment = stripSupplierPrefix(location.pathname).split('/').filter(Boolean)[0];
    if (!segment || !isNavSegmentDisabled({ segment, roles: user.roles })) {
      return;
    }
    if (isCreditAppRestricted(user.roles)) {
      navigate(supplierLandingPath('/creditApplications'), { replace: true });
    } else if (
      getNavSegmentSection(segment) === 'CREDIT' &&
      (supplierId || user.roles.includes('SUB_SUPPLIER'))
    ) {
      navigate('/supplier', { replace: true });
    }
  }, [location.pathname, user?.id, isLoggedIn, supplierId]);

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    if (
      location.pathname &&
      !location.pathname.startsWith('/login') &&
      !location.pathname.startsWith('/refer?')
    ) {
      handleSavePastPath(currentPath);
    }

    return () => {};
  }, [location.pathname]);

  useEffect(() => {
    if (myFirebaseAuth.currentUser && firebaseUserId && isPasswordSet) {
      setIsLoggedIn(true);
      console.log('user logged in');
    } else {
      setIsLoggedIn(false);
      console.log('user not logged in');
      const pastPath = location.pathname;
      let loginPagePath = '/login';
      if (pastPath && pastPath.startsWith('/companies/')) {
        const pathSplit = pastPath.split('/');
        if (pathSplit.length > 3) {
          loginPagePath = `/login/${pathSplit[4]}`;
        } else {
          loginPagePath = '/login';
        }
      } else {
        loginPagePath = '/login';
      }
      navigate(loginPagePath);
    }
    if (!user && myFirebaseAuth.currentUser && firebaseUserId && isPasswordSet) {
      dispatch(getUserDetails());
    }
  }, [myFirebaseAuth.currentUser]);

  useEffect(() => {
    (async () => {
      if (myFirebaseAuth.currentUser && isLoggedIn && isPasswordSet) {
        // Navigate to a specific path depending on the user roles
        if (user && firebaseUserId) {
          LogRocket.identify(user.id, {
            email: user.email,
            name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
          });
          const mainRoles = user.roles.filter((role) => role !== 'DILL_ADMIN');

          const isArSalesOnly = mainRoles.includes('AR_SALES') && !mainRoles.includes('AR_ALL');
          // Credit apps wins over the supplier/lien landings below for these users.
          const isCreditAppOnly = isCreditAppRestricted(user.roles);
          const masonWestSupplierId = 'uy0jvcnqZaghfC3wgqUB';
          // Main buyers of the supplier the user logged in through come first, so the `[0]` picks
          // below land on that supplier's company rather than the alphabetically-first one. The
          // redux value is dropped on reload, so fall back to the id saved at login.
          const loginSupplierId = basicSupplier?.id || getLoginSupplierId();
          const filteredMainBuyers = sortMainBuyersByLoginSupplier(
            (user?.userMainBuyers || []).filter((mb) => mb?.syncSupplierId !== masonWestSupplierId),
            loginSupplierId
          );
          const defaultMainBuyer = filteredMainBuyers[0];
          const mainPath = location.pathname;
          // Handle email links with old routes
          if (mainPath.startsWith('/companies/invoices') && user.userMainBuyers && !isPreviewMode) {
            const mainPathSplit = mainPath.split('/');
            if (mainPathSplit.length === 5) {
              const supplierDomainId = mainPathSplit[4];
              const buyerName = decodeURIComponent(mainPathSplit[3].split('__')[0])
                .replaceAll(' ', '_')
                .replaceAll('+', '_')
                .replaceAll('_', ' ');

              const mainBuyer = user.userMainBuyers?.find((mb) => mb.name === buyerName);
              let newNavPath = '';
              if (mainBuyer) {
                newNavPath = `/companies/${mainBuyer.id}/invoices/${supplierDomainId}`;
              } else if (defaultMainBuyer) {
                newNavPath = `/companies/${defaultMainBuyer.id}/invoices`;
              }
              if (newNavPath) {
                navigate(newNavPath);
              }
            }
            if (mainPathSplit.length === 4) {
              const buyerName = decodeURIComponent(mainPathSplit[3].split('__')[0])
                .replaceAll(' ', '_')
                .replaceAll('+', '_')
                .replaceAll('_', ' ');

              const mainBuyer = filteredMainBuyers?.find((mb) => mb.name === buyerName);
              let newNavPath = '';
              if (mainBuyer) {
                newNavPath = `/companies/${mainBuyer.id}/invoices`;
              } else if (defaultMainBuyer) {
                newNavPath = `/companies/${defaultMainBuyer.id}/invoices`;
              }
              if (newNavPath) {
                navigate(newNavPath);
              }
            }
          }
          if (mainPath.startsWith('/buyerLienReleases/overview') && user.userMainBuyers) {
            const searchParams = new URLSearchParams(location.search);
            const email = searchParams.get('email');
            let releaseId = searchParams.get('releaseId');
            let extraPath = '';
            if (!releaseId && mainPath.startsWith('/buyerLienReleases/overview/editRelease')) {
              const mainPathSplit = mainPath.split('/');
              if (mainPathSplit.length === 5) {
                releaseId = mainPathSplit[4];
                // releaseId = '';
                extraPath = `/editRelease/${releaseId}`;
              }
            }
            if (email && releaseId) {
              const result = await dispatch(
                getReleaseBySupplier({ supplierId: currentSupplier?.id || '', releaseId })
              );
              console.log({ result });
              if (result.type === 'releases/getReleaseBySupplier/fulfilled') {
                const payload = result.payload as any;
                const mainBuyerId = payload.data.buyer?.id;
                const mainBuyer = filteredMainBuyers?.find((mb) => mb.id === mainBuyerId);
                let newNavPath = '';
                if (mainBuyer) {
                  // navigate(`/companies/${mainBuyer.id}/invoices/${supplierDomainId}`);
                  newNavPath = `/buyerLienReleases/${mainBuyer.id}/overview${extraPath}${location.search}`;
                } else if (defaultMainBuyer) {
                  newNavPath = `/buyerLienReleases/${defaultMainBuyer.id}/overview`;
                }
                if (!newNavPath) {
                  return;
                }
                navigate(newNavPath, { replace: false });
                navigate(0);
                return;
              }
            }
          }
          // Handle legacy routes such as /buyerLienReleases/overview, /companies/invoices, etc
          if (
            LEGACY_BASE_PATHS.some((route) => mainPath.startsWith(route)) &&
            selectedMainBuyerId &&
            user.userMainBuyers &&
            filteredMainBuyers.length > 0 &&
            LEGACY_SUB_PATHS.includes(selectedMainBuyerId)
          ) {
            const urlParts = location.pathname.split('/');

            if (urlParts.length > 1 && defaultMainBuyer) {
              navigate(`/${urlParts[1]}/${defaultMainBuyer.id}/${selectedMainBuyerId}`);
            }
          }
          if (mainRoles.length > 0) {
            if (
              !mainRoles.includes('SUPPLIER') &&
              ['/customers', '/invoices', '/salesReceipts', '/payments', '/supplier'].some((path) =>
                stripSupplierPrefix(mainPath).startsWith(path)
              )
            ) {
              if (hasCreditAppRole(mainRoles)) {
                navigate(supplierLandingPath('/creditApplications'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('BUYER') && selectedMainBuyerId) {
                navigate(`/companies/${selectedMainBuyerId}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/companies/${defaultMainBuyer.id}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (mainRoles.includes('MAIN_BUYER') && selectedMainBuyerId) {
                navigate(`/buyerLienReleases/${selectedMainBuyerId}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('MAIN_BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/buyerLienReleases/${defaultMainBuyer.id}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (mainRoles.includes('SUB_SUPPLIER')) {
                navigate('/lienReleases');
                localStorage.setItem('selectAccountType', 'Supplier');
              }
            }

            if (
              !hasCreditAppRole(mainRoles) &&
              !mainRoles.includes('SUPPLIER') &&
              [
                '/creditApplications',
                '/creditMonitoring',
                '/creditApplicationRequestTemplates',
                '/supplier',
              ].some((path) => stripSupplierPrefix(mainPath).startsWith(path))
            ) {
              if (mainRoles.includes('SUB_SUPPLIER')) {
                navigate('/lienReleases');
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('BUYER') && selectedMainBuyerId) {
                navigate(`/companies/${selectedMainBuyerId}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/companies/${defaultMainBuyer.id}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (mainRoles.includes('MAIN_BUYER') && selectedMainBuyerId) {
                navigate(`/buyerLienReleases/${selectedMainBuyerId}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('MAIN_BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/buyerLienReleases/${defaultMainBuyer.id}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              }
            }

            if (
              !mainRoles.includes('SUB_SUPPLIER') &&
              ['/lienReleases', '/supplier'].some((path) =>
                stripSupplierPrefix(mainPath).startsWith(path)
              )
            ) {
              if (isCreditAppOnly) {
                navigate(supplierLandingPath('/creditApplications'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (isArSalesOnly) {
                navigate(supplierLandingPath('/paymentRequests'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('SUPPLIER')) {
                navigate(supplierLandingPath('/customers'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('BUYER') && selectedMainBuyerId) {
                navigate(`/companies/${selectedMainBuyerId}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/companies/${defaultMainBuyer.id}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (mainRoles.includes('MAIN_BUYER') && selectedMainBuyerId) {
                navigate(`/buyerLienReleases/${selectedMainBuyerId}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('MAIN_BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/buyerLienReleases/${defaultMainBuyer.id}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              }
            }

            if (
              !mainRoles.includes('BUYER') &&
              !isPreviewMode &&
              ['/companies'].some((path) => stripSupplierPrefix(mainPath).startsWith(path))
            ) {
              if (mainRoles.includes('MAIN_BUYER') && selectedMainBuyerId) {
                navigate(`/buyerLienReleases/${selectedMainBuyerId}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('MAIN_BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/buyerLienReleases/${defaultMainBuyer.id}/overview`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (isCreditAppOnly) {
                navigate(supplierLandingPath('/creditApplications'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('SUB_SUPPLIER')) {
                navigate('/lienReleases');
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (isArSalesOnly) {
                navigate(supplierLandingPath('/paymentRequests'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('SUPPLIER')) {
                navigate(supplierLandingPath('/customers'));
                localStorage.setItem('selectAccountType', 'Supplier');
              }
            }
            if (
              !mainRoles.includes('MAIN_BUYER') &&
              ['/buyerLienReleases', '/adminTemplates'].some((path) =>
                stripSupplierPrefix(mainPath).startsWith(path)
              )
            ) {
              if (mainRoles.includes('BUYER') && selectedMainBuyerId) {
                navigate(`/companies/${selectedMainBuyerId}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (
                mainRoles.includes('BUYER') &&
                user?.userMainBuyers &&
                user.userMainBuyers?.length > 0 &&
                defaultMainBuyer
              ) {
                navigate(`/companies/${defaultMainBuyer.id}/invoices`);
                localStorage.setItem('selectAccountType', 'Buyer');
              } else if (isCreditAppOnly) {
                navigate(supplierLandingPath('/creditApplications'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('SUB_SUPPLIER')) {
                navigate('/lienReleases');
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (isArSalesOnly) {
                navigate(supplierLandingPath('/paymentRequests'));
                localStorage.setItem('selectAccountType', 'Supplier');
              } else if (mainRoles.includes('SUPPLIER')) {
                navigate(supplierLandingPath('/customers'));
                localStorage.setItem('selectAccountType', 'Supplier');
              }
            }
          }
        }
      }
    })();
  }, [user?.id, isLoggedIn, selectedMainBuyerId, currentSupplier?.id]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailFromLink = searchParams.get('email');
    const mainPath = location.pathname;
    if (location.pathname) {
      if (emailFromLink) {
        sessionStorage.setItem('emailFromLink', emailFromLink);
      }
      if (
        [
          '/customers',
          '/invoices',
          '/supplierJobs',
          '/salesReceipts',
          '/payments',
          '/lienReleases',
        ].some((path) => stripSupplierPrefix(mainPath).startsWith(path))
      ) {
        localStorage.setItem('selectAccountType', 'Supplier');
      }
      if (
        ['/buyerLienReleases', '/companies'].some((path) =>
          stripSupplierPrefix(mainPath).startsWith(path)
        )
      ) {
        localStorage.setItem('selectAccountType', 'Buyer');
      }
    }

    if (
      emailFromLink &&
      myFirebaseAuth.currentUser &&
      myFirebaseAuth.currentUser.email !== emailFromLink
    ) {
      sessionStorage.setItem('emailFromLink', emailFromLink);
      // localStorage.removeItem('pastPathList');
      // localStorage.removeItem('referPath');
      dispatch(logoutUser());
    }
    return () => {};
  }, [location.pathname, firebaseUserId]);

  return <React.Fragment>{isLoggedIn ? children : null}</React.Fragment>;
};
